/**
 * Shared rules for Studio onboarding vs Artist profile creation.
 * Keep in sync with backend user flags (needsStudioOnboarding, onboardingCompleted, studioOnboardingPhase).
 */

export function hasValidActiveMembership(payload) {
  if (!payload) return false;
  const m = payload.membership || payload;
  const status = (m.status || '').toString().toLowerCase();
  const isActiveLike = status === 'active' || status === 'trialing';
  if (!isActiveLike) return false;

  const endRaw = m.expiresAt || m.currentPeriodEnd || m.trialEndsAt || payload.expiresAt;
  if (!endRaw) return true;
  const endDate = typeof endRaw?.toDate === 'function' ? endRaw.toDate() : new Date(endRaw);
  if (Number.isNaN(endDate.getTime())) return true;
  return endDate.getTime() > Date.now();
}

export function membershipPeriodEnded(payload) {
  if (!payload) return false;
  const m = payload.membership || payload;
  if (m.isExpired === true) return true;
  const endRaw = m.expiresAt || m.currentPeriodEnd || m.trialEndsAt || payload.expiresAt;
  if (!endRaw) return false;
  const endDate = typeof endRaw?.toDate === 'function' ? endRaw.toDate() : new Date(endRaw);
  if (Number.isNaN(endDate.getTime())) return false;
  return endDate.getTime() <= Date.now();
}

/** Active/trialing + dates, or explicit allowed when period has not ended. */
export function contextAllowsEntitlement(payload) {
  if (!payload) return false;
  if (membershipPeriodEnded(payload)) return false;
  if (hasValidActiveMembership(payload)) return true;
  if (payload.allowed === true) return true;
  return false;
}

/**
 * Routes a student may visit while `needsStudioOnboarding && onboardingCompleted !== true`.
 * `/artist-profile` is only included after studio access exists (subscription, Artmo redemption, etc.)
 * or onboarding is explicitly completed — not when the user only dismissed the membership modal.
 */
export function buildStudioOnboardingAllowedPaths(membership) {
  const paths = new Set([
    '/onboarding/studio-access',
    '/onboarding/artmo-success',
    '/membership',
    '/create-books',
    '/my-art-world',
    '/my-art-history',
    '/my-art-stories',
    '/activity-dashboard',
    '/learn-art-history',
  ]);
  const allowProfile =
    membership?.onboardingCompleted === true || contextAllowsEntitlement(membership);
  if (allowProfile) {
    paths.add('/artist-profile');
  }
  return paths;
}

/** True when Firestore/API still mark the post-signup wizard as incomplete. */
export function studioOnboardingFlagsPending(membership, userDoc) {
  const done =
    userDoc?.onboardingCompleted === true ||
    membership?.onboardingCompleted === true ||
    userDoc?.needsStudioOnboarding === false ||
    membership?.needsStudioOnboarding === false;
  if (done) return false;
  return (
    userDoc?.needsStudioOnboarding === true || membership?.needsStudioOnboarding === true
  );
}

/**
 * Only send users to /onboarding/studio-access when flags say "pending" AND they lack studio entitlement.
 * Active Artmo/subscription accounts often keep stale flags if the wizard was dismissed with X.
 */
export function shouldForceStudioOnboarding(membership, userDoc) {
  if (!studioOnboardingFlagsPending(membership, userDoc)) return false;
  if (contextAllowsEntitlement(membership)) return false;
  // Membership API blocked (CORS/outage): do not trap users in the wizard when entitlement cannot be verified.
  if (membership == null) return false;
  return true;
}

/** Whether first-time artist profile creation should be allowed (Artist Corner → profile setup). */
export function canAccessArtistProfileCreation(membership) {
  // Unknown until /membership/status loads — do not send users to profile creation prematurely.
  if (membership == null) return false;
  if (membership.onboardingCompleted === true) return true;
  if (membership.needsStudioOnboarding === true && membership.onboardingCompleted !== true) {
    return contextAllowsEntitlement(membership);
  }
  return true;
}
